import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, Sparkles } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { LangSwitcher } from "../components/LangSwitcher";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", business_name: "",
  interest: "", notes: "", consent_accepted: false,
};

export default function PublicLead() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [ctx, setCtx] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(empty);
  const [eventId, setEventId] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(Date.now());
  const honeypot = useRef("");

  useEffect(() => {
    api.get(`/public/capture/${token}`)
      .then(({ data }) => {
        setCtx(data);
        if (!data.active_event && data.selectable_events?.length === 1) {
          setEventId(data.selectable_events[0].id);
        }
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return toast.error(t("capture.nameRequired"));
    if (!form.email.trim() && !form.phone.trim()) return toast.error(t("capture.contactRequired"));
    if (!form.consent_accepted) return toast.error(t("capture.consentRequired"));
    setBusy(true);
    try {
      const { data } = await api.post(`/public/capture/${token}`, {
        ...form,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        language: i18n.language.slice(0, 2),
        event_id: ctx.active_event ? null : (eventId || null),
        page_url: window.location.href,
        referrer: document.referrer || null,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),        honeypot: honeypot.current,
        elapsed_ms: Date.now() - started.current,
      });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6" data-testid="capture-not-found">
        <p className="text-center text-sm text-muted-foreground">{t("capture.invalid")}</p>
      </main>
    );
  }
  if (!ctx) return <div className="p-8 text-sm text-muted-foreground">{t("common.loading")}</div>;

  const eventLabel = ctx.active_event?.event_title
    || ctx.selectable_events?.find((e) => e.id === eventId)?.title;

  if (done) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center"
        data-testid="capture-success">
        <CheckCircle2 className="h-16 w-16 text-[hsl(var(--success))]" />
        <h1 className="font-display text-2xl font-extrabold">{t("capture.thanksTitle")}</h1>
        <p className="max-w-sm text-sm text-muted-foreground">{t("capture.thanksText")}</p>
        {done.event_title && <p className="text-xs font-semibold text-muted-foreground">{done.event_title}</p>}
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-muted/40 pb-16" data-testid="capture-page">
      <header className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
        <span className="font-display text-lg font-bold">Vytali<span className="text-accent">Days</span></span>
        <div className="ml-auto"><LangSwitcher /></div>
      </header>

      <div className="mx-auto max-w-[560px] px-4 pt-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-accent">
          <Sparkles className="h-3.5 w-3.5" />{t("capture.eyebrow")}
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight sm:text-4xl">
          {t("capture.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground" data-testid="capture-attribution">
          {t("capture.withAmbassador", { name: ctx.ambassador_name })}
          {eventLabel ? ` · ${eventLabel}` : ""}
        </p>

        <form onSubmit={submit} className="vy-card mt-5 space-y-4 p-5" data-testid="capture-form">
          {!ctx.active_event && ctx.selectable_events?.length > 0 && (
            <label className="block text-xs font-bold uppercase text-muted-foreground">
              {t("capture.chooseEvent")}
              <select data-testid="capture-event-select" value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="mt-1 h-12 w-full rounded-md border border-input bg-background px-3 text-sm font-semibold text-foreground">
                <option value="">{t("capture.noEvent")}</option>
                {ctx.selectable_events.map((e) => (
                  <option key={e.id} value={e.id}>{e.title}</option>
                ))}
              </select>
            </label>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Input data-testid="capture-first-name" className="h-12" placeholder={t("capture.firstName")}
              value={form.first_name} onChange={set("first_name")} />
            <Input data-testid="capture-last-name" className="h-12" placeholder={t("capture.lastName")}
              value={form.last_name} onChange={set("last_name")} />
          </div>
          <Input data-testid="capture-phone" className="h-12" type="tel" placeholder={t("capture.phone")}
            value={form.phone} onChange={set("phone")} />
          <Input data-testid="capture-email" className="h-12" type="email" placeholder={t("capture.email")}
            value={form.email} onChange={set("email")} />
          <Input data-testid="capture-business" className="h-12" placeholder={t("capture.business")}
            value={form.business_name} onChange={set("business_name")} />
          <Input data-testid="capture-interest" className="h-12" placeholder={t("capture.interest")}
            value={form.interest} onChange={set("interest")} />
          <Textarea data-testid="capture-notes" rows={3} placeholder={t("capture.notes")}
            value={form.notes} onChange={set("notes")} />
          <input tabIndex={-1} autoComplete="off" aria-hidden="true"
            className="hidden" onChange={(e) => { honeypot.current = e.target.value; }} />

          <label className="flex items-start gap-3 text-sm">
            <Checkbox data-testid="capture-consent" checked={form.consent_accepted}
              onCheckedChange={(v) => setForm({ ...form, consent_accepted: !!v })} />
            <span className="text-muted-foreground">{t("capture.consent")}</span>
          </label>

          <Button data-testid="capture-submit" type="submit" disabled={busy}
            className="h-14 w-full rounded-full font-bold">
            {busy ? t("common.sending") : t("capture.submit")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t("capture.privacy")}</p>
        </form>
      </div>
    </main>
  );
}
